import { Pencil, Plus, Trash2, Save, X, GripVertical, List } from 'lucide-preact'
import type { UseBenefitsListReturn } from '@/hooks/useBenefitsList'
import { useDragReorder } from '@/hooks/useDragReorder'
import { useReorderFocus } from '@/hooks/useReorderFocus'

type BenefitsCardProps = UseBenefitsListReturn & { title: string; canEdit?: boolean }

export function BenefitsCard({ title, canEdit = true, ...b }: BenefitsCardProps) {
  const { isDragOver, dragProps } = useDragReorder(b.move)
  const { listRef, focusAfterMove } = useReorderFocus()

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <List className="w-5 h-5 text-primary-600" /> {title}
        </h3>
        {!b.editing ? (
          canEdit && (
            <button type="button" onClick={() => b.setEditing(true)} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700">
              <Pencil className="w-4 h-4" /> Edit
            </button>
          )
        ) : (
          <div className="flex items-center gap-2">
            <button type="button" onClick={b.save} disabled={b.saving || b.hasDirtyEmpty || !b.benefitsChanged} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
              <Save className="w-4 h-4" /> Save
            </button>
            <button type="button" onClick={b.cancel} disabled={b.saving} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        )}
      </div>
      {!b.editing ? (
        <div className="mt-3 space-y-2">
          {b.normInitial.length > 0 ? b.normInitial.map((text, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-xs mt-1 w-5 h-5 flex items-center justify-center rounded-full bg-primary-600 text-white font-medium">{i + 1}</span>
              <span className="text-sm text-slate-700 dark:text-slate-300">{text}</span>
            </div>
          )) : <p className="text-slate-500 dark:text-slate-400 text-sm">No benefits yet.</p>}
        </div>
      ) : (
        <div className="mt-3 space-y-3" ref={listRef}>
          {b.fields.map((field, idx) => {
            const val = (b.watchedBenefits[idx]?.value || '').trim()
            const fs = b.getFieldState(`benefits.${idx}.value`, b.formState)
            const showErr = fs.isDirty && val.length === 0
            return (
              <div
                key={field.id}
                className={`flex items-start gap-2 p-2 rounded-md border transition-colors ${isDragOver(idx) ? 'border-primary-400 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20' : 'border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900'}`}
                {...dragProps(idx)}
              >
                <div className="flex items-center gap-1 pr-1 select-none">
                  {/*
                    Focusable, with a key handler, matching the agenda and speaker lists. It was
                    tabIndex={-1} with no onKeyDown, which made dragging the only route and left
                    keyboard-only users unable to reorder benefits at all.
                  */}
                  <button
                    type="button"
                    data-reorder-grip
                    className="cursor-grab p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    tabIndex={0}
                    aria-label={`Reorder benefit ${idx + 1}. Use Arrow Up / Arrow Down to move.`}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowUp' && idx > 0) { e.preventDefault(); b.move(idx, idx - 1); focusAfterMove(idx - 1) }
                      else if (e.key === 'ArrowDown' && idx < b.fields.length - 1) { e.preventDefault(); b.move(idx, idx + 1); focusAfterMove(idx + 1) }
                    }}
                  >
                    <GripVertical className="w-5 h-5" />
                  </button>
                  <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400 w-4 text-center">{idx + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      maxLength={160}
                      {...b.register(`benefits.${idx}.value`)}
                      className={`flex-1 px-2.5 py-1.5 rounded-md border text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 ${showErr ? 'border-red-400 dark:border-red-700 focus:ring-red-500 dark:focus:ring-red-400' : 'border-slate-300 dark:border-slate-600 focus:ring-primary-500'}`}
                      placeholder="Benefit"
                    />
                    <button type="button" onClick={() => b.remove(idx)} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20" aria-label="Delete benefit">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[11px] text-slate-500 dark:text-slate-400">{val.length}/160</span>
                    {showErr && <div className="text-[11px] text-red-600 dark:text-red-400">Required</div>}
                  </div>
                </div>
              </div>
            )
          })}
          <button type="button" onClick={() => b.append({ value: '' })} className="inline-flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      )}
    </div>
  )
}
